import { useState } from "react";
import { Loader2, Lock, Settings } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { BoardContextProvider, useBoardContextContext } from "./context/BoardContextContext";
import { SetupForm } from "./components/SetupForm";
import { PagePickerStep } from "./components/PagePickerStep";
import { AnalysingLoader } from "./components/AnalysingLoader";
import { QuestionsStep } from "./components/QuestionsStep";
import { ContextView } from "./components/ContextView";
import type { SubcategoryDetailProps, Subcategory } from "../../constants/settingsStructure";
import { OpenAIKeyModal } from "../../components/OpenAIKeyModal";
import { useBoardPermissions } from "../../hooks/useBoardPermissions";

const BoardContextPageHeader = ({ subcategory }: { subcategory: Subcategory }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
    <p className="text-sm text-muted-foreground">{subcategory.description}</p>
  </div>
);

const BoardContextContent = ({ subcategory }: { subcategory: Subcategory }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { step, isInitialising, hasOpenAIKey } = useBoardContextContext();
  const { canWrite } = useBoardPermissions();

  if (isInitialising) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasOpenAIKey) {
    return (
      <>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-6 pt-6">
              <BoardContextPageHeader subcategory={subcategory} />

              <div className="flex w-full items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg mb-5">
                <Lock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    OpenAI API key required
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    An API key is needed to generate board context from your business sources.
                    {canWrite
                      ? " Click Configure to add your API key."
                      : " Only the Board Owner can configure the API key."}
                  </p>
                </div>
                {canWrite && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsModalOpen(true)}
                    className="shrink-0"
                  >
                    <Settings className="h-4 w-4 mr-1.5" />
                    Configure
                  </Button>
                )}
              </div>
            </div>
            <SetupForm />
          </div>
        </div>
        <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  if (step === "setup") {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 pt-6">
            <BoardContextPageHeader subcategory={subcategory} />
          </div>
          <SetupForm />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {step === "page-picker" && <PagePickerStep />}
      {step === "analysing" && <AnalysingLoader variant="questions" />}
      {step === "questions" && <QuestionsStep />}
      {step === "building" && <AnalysingLoader variant="context" />}
      {step === "context-view" && <ContextView />}
    </div>
  );
};

const BoardContextDetail = ({ subcategory }: SubcategoryDetailProps) => (
  <BoardContextProvider>
    <BoardContextContent subcategory={subcategory} />
  </BoardContextProvider>
);

export default BoardContextDetail;
